import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Wand2, ArrowRight } from 'lucide-react';
import { useApp } from '@/store/app';
import { routeIntent } from '@/lib/intent';
import { findToolById } from '@/registry';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

interface Message {
  id: string;
  role: 'user' | 'agent';
  text: string;
  toolId?: string;
}

const SUGGESTIONS = [
  'Merge two PDFs into one',
  'Compress this image to 200KB',
  'Generate a strong 24-char password',
  'Convert 5000 INR to USD',
  'Make a QR code for my website',
  'Pretty-print this JSON',
  'Build a 5-color palette from #6366f1',
];

export function AgentPanel() {
  const { agentOpen, setAgentOpen } = useApp();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'agent',
      text: "Hi — I'm your utility agent. Drop a file or describe a task and I'll route you to the best tool. I can chain steps, e.g. \"OCR → extract table → clean CSV → export Excel\".",
    },
  ]);
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  function handleSend(text?: string) {
    const value = (text ?? input).trim();
    if (!value) return;

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', text: value };
    const intent = routeIntent(value);
    const tool = intent.toolId ? findToolById(intent.toolId) : null;

    const agentMsg: Message = {
      id: `a-${Date.now()}`,
      role: 'agent',
      text: tool
        ? `${intent.reason}. Opening **${tool.name}** — your input is ready to drop in.`
        : `I couldn't pinpoint a single tool. Here are the closest matches: ${intent.matches
            .map((m) => m.name)
            .join(', ')}.`,
      toolId: tool?.id,
    };

    setMessages((m) => [...m, userMsg, agentMsg]);
    setInput('');
  }

  return (
    <AnimatePresence>
      {agentOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setAgentOpen(false)}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 z-50 h-screen w-[min(440px,calc(100vw-2rem))] bg-bg-elev border-l border-border shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="h-14 px-5 border-b border-border flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm rotate-[-3deg]">
                  <Sparkles size={15} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-none">Utility Agent</div>
                  <div className="text-2xs text-fg-subtle mt-1">Intelligent tool routing</div>
                </div>
              </div>
              <button
                onClick={() => setAgentOpen(false)}
                className="size-7 rounded-md hover:bg-surface-hover flex items-center justify-center text-fg-muted hover:text-fg transition"
              >
                <X size={14} />
              </button>
            </div>

            {/* Messages */}
            <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  onOpen={(id) => {
                    navigate(`/tool/${id}`);
                    setAgentOpen(false);
                  }}
                />
              ))}

              {messages.length === 1 && (
                <div className="pt-4">
                  <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">
                    Try something
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        className="group text-left px-3 py-2 text-sm rounded-lg border border-border hover:border-accent/40 hover:bg-accent/5 transition flex items-center gap-2"
                      >
                        <Wand2 size={12} className="text-orange-500 dark:text-amber-400 shrink-0" />
                        <span className="flex-1">{s}</span>
                        <ArrowRight
                          size={12}
                          className="text-fg-subtle opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border p-3 shrink-0">
              <div className="flex items-end gap-2 bg-bg-subtle border border-border rounded-xl p-1.5 focus-within:border-accent transition">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Describe a task…"
                  rows={1}
                  className="flex-1 resize-none bg-transparent border-0 outline-none px-2 py-1.5 text-sm placeholder:text-fg-subtle max-h-32"
                />
                <Button
                  variant="primary"
                  size="icon-sm"
                  onClick={() => handleSend()}
                  disabled={!input.trim()}
                  className="shrink-0"
                >
                  <Send size={12} />
                </Button>
              </div>
              <div className="mt-2 text-2xs text-fg-subtle px-1 flex items-center justify-between">
                <span>Press <kbd>⏎</kbd> to send · <kbd>⇧⏎</kbd> for newline</span>
                <span><kbd>⌘J</kbd></span>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function MessageBubble({
  message,
  onOpen,
}: {
  message: Message;
  onOpen: (toolId: string) => void;
}) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] px-3.5 py-2.5 text-sm rounded-2xl leading-relaxed',
          isUser
            ? 'bg-accent text-accent-fg'
            : 'bg-bg-subtle border border-border',
        )}
      >
        <span dangerouslySetInnerHTML={{ __html: renderInline(message.text) }} />
        {message.toolId && (
          <button
            onClick={() => onOpen(message.toolId!)}
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 h-7 text-xs font-medium rounded-md bg-bg border border-border hover:border-accent/40 hover:bg-accent/5 transition w-full justify-between"
          >
            <span className="text-fg">Open tool</span>
            <ArrowRight size={12} className="text-accent" />
          </button>
        )}
      </div>
    </div>
  );
}

function renderInline(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}
